import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/dist/query/react';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { Mutex } from 'async-mutex';

import { API_END_POINT, API_VERSION, END_POINTS } from '@config';
import {
  clearAllLocalStorageItems,
  getLocalStorageItem,
  setTokensInStorage,
  StorageKeys
} from '@utils/storage';
import { ApiError, RefreshTokenResponse, TokenResponse } from '@types';
import useNotify from '@hooks/notify';
import { AUTH } from '@constants/routes';

const mutex = new Mutex();

const handle403And500Error = (errorCode: string) => {
  // window.location.replace(
  //   `/error/${Object.keys(HTTPStatus).includes(errorCode) ? errorCode : '404'}`
  // );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { createErrorAlert } = useNotify();
  createErrorAlert(
    `${errorCode}: Something went wrong. Please try again later`
  );
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_END_POINT}${API_VERSION}`,
  prepareHeaders: (headers) => {
    const token = getLocalStorageItem(StorageKeys.ACCESS_TOKEN);
    const schoolId = getLocalStorageItem(StorageKeys.SCHOOL_ID);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (schoolId) {
      headers.set('x-school-id', schoolId);
    }
    return headers;
  }
});

const baseQueryWithReauth: BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError,
  {
    skipAuthRedirect?: boolean;
  }
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (
    result.error &&
    result.error.status === 401 &&
    !extraOptions?.skipAuthRedirect
  ) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = getLocalStorageItem(StorageKeys.REFRESH_TOKEN);
        const accessToken = getLocalStorageItem(StorageKeys.ACCESS_TOKEN);
        const refreshResult = await baseQuery(
          {
            url: END_POINTS.AUTH.REFRESH,
            method: 'POST',
            body: { refreshToken, accessToken }
          },
          api,
          extraOptions
        );
        const refreshData = refreshResult as { data: RefreshTokenResponse };
        if (refreshData?.data) {
          setTokensInStorage(refreshData.data.data as TokenResponse);
          // Retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          window.location.replace(AUTH.LOGIN);
          clearAllLocalStorageItems();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  } else if (result.error) {
    const error = result.error as ApiError;
    const errorCode = error.data?.statusCode;
    if (
      (error.status === 403 || error.status === 404) &&
      errorCode &&
      !args?.url.includes('auth')
    ) {
      handle403And500Error(errorCode);
      // TODO: might need sepeate cases in future
    } else if (error.status === 500 && errorCode) {
      handle403And500Error(errorCode);
    }
  }
  return result;
};

// eslint-disable-next-line import/prefer-default-export
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: false,
  refetchOnReconnect: false,
  tagTypes: ['Branches', 'Calendar']
});
